import { AfterViewInit, Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { getNamedEnum } from 'src/app/_helpers/enum-helper';
import { SampleMediaDetailsPopupComponent } from 'src/app/common-components/sample-media-details-popup/sample-media-details-popup.component';
import { MediaTypeEnum } from 'src/app/enums/mediaType.enum';
import { SampleMediaDialogComponent } from 'src/app/modals/sample-media-dialog/sample-media-dialog.component';
import { SampleMedia } from 'src/app/models/sampleMedia.model';
import { PhaseEnum, PhaseEnumDescriptions } from 'src/app/enums/phase.enum';
import { UserService } from 'src/app/services/user.service';
import { AuthService } from 'src/app/services';
import { CommonService } from 'src/app/services/common.service';
import { GridViewService } from 'src/app/services/grid-view.service';
import { SampleMediaService } from 'src/app/services/sample-media.service';
import { ToastService } from 'src/app/services/toast.service';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { segsRouteConfig } from 'src/app/shared/constants/segsRouteConfig.constants';
import { DeleteDialog } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { IFormDialogData } from 'src/app/shared/form/form.component';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { ChildColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { Lookup } from 'src/app/models/lookup.model';
import { FlyoutContainerComponent } from 'src/app/shared/flyout/flyout-container/flyout-container.component';

@Component({
  standalone: false,
  selector: 'app-sample-media',
  templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss', './sample-media.component.scss']
})
export class SampleMediaComponent extends CoreGridComponent<SampleMedia> implements OnInit, AfterViewInit {
  @ViewChild(FlyoutContainerComponent) flyout!: FlyoutContainerComponent;
  users: Lookup[] = [];

  constructor(
    protected sampleMediaService: SampleMediaService,
    toastService: ToastService,
    commonService: CommonService,
    dialog: MatDialog,
    authService: AuthService,
    gridViewService: GridViewService,
    gridSettingsService: GridSettingsService,
    private userService: UserService
  ) {
    super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService);
    this.gridService = sampleMediaService;
  }

  get hasOrgFilter(): boolean {
    return true;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.userService.getAll().subscribe(users => this.users = users);
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.loadHeaderData();
  }

  protected get objectId(): string {
    return RolePermissionObjects.SampleMedia;
  }

  protected override get isGridViewEnabled(): boolean { return true; }

  override initializeProperties(): void {
    this.addEntityTitle = 'Sample Media';
    this.searchPlaceholder = 'Search Sample Media';
  }

  override initializeColumns(): void {
    this.additionalActions = [];
    this.columnConfigs = [
      {
        name: nameOf<SampleMedia>(x => x.id),
        displayName: 'Media ID',
        cssClass: () => 'left-aligned cursor-pointer',
        headerCssClass: () => 'left-aligned',
        isDisplayNameConfigurable: false,
        onClick: (row: SampleMedia) => {
          this.openFlyoutInternal(SampleMediaDetailsPopupComponent, row.id, '80vw', true);
        }
      },
      { name: nameOf<SampleMedia>(x => x.name), displayName: 'Name' },
      {
        name: nameOf<SampleMedia>(x => x.mediaType), displayName: 'Type',
        cellTemplate: (row) => getNamedEnum(MediaTypeEnum)[row.mediaType],
        enumFilterMap: this.enumToValueNameArray(MediaTypeEnum)
      },
      { name: nameOf<SampleMedia>(x => x.manufacturer), displayName: 'Manufacturer' },
      { name: nameOf<SampleMedia>(x => x.poreSizeMicrons), displayName: 'Pore Size (µm)' },
      { name: nameOf<SampleMedia>(x => x.diameterMM), displayName: 'Diameter (mm)' },
      { name: nameOf<SampleMedia>(x => x.sorbentMaterial), displayName: 'Sorbent Material' },
      { name: nameOf<SampleMedia>(x => x.phase), displayName: 'Phase', cellTemplate: (row) => PhaseEnumDescriptions[row.phase], enumFilterMap: this.enumToValueNameArray(PhaseEnum) },
      { name: nameOf<SampleMedia>(x => x.recommendedFlowRateSccm), displayName: 'Flow Rate (sccm)' },
      { name: nameOf<SampleMedia>(x => x.usageNotes), displayName: 'Usage Notes' },
      { name: nameOf<SampleMedia>(x => x.createdByUserId), displayName: 'Created By', cellTemplate: (row) => this.users?.find(u => u.id.toString() === row.createdByUserId)?.name || '-' },
      {
        name: nameOf<SampleMedia>(x => x.isActive), displayName: 'Active', cellTemplate: () => '',
        children: (row) => [
          new ChildColumnConfig<SampleMedia>({
            cellTemplate: (row) => '',
            cssClass: (row) => row.isActive ? 'checked custom-div-switch' : 'custom-div-switch',
            onClick: (row) => this.onSwitchChange(row)
          }),
        ],
      },
      { name: 'action', displayName: 'Actions' }
    ];
  }

  override objectFilterMap(propertyName: string): any | undefined {
    switch (propertyName) {
      case nameOf<SampleMedia>(x => x.createdByUserId): return this.userService;
      default: return undefined;
    }
  }

  override initializeTabs(): any[] {
    return segsRouteConfig;
  }

  override initializeOverviewConfigs(): void {
    this.overviewConfigs = [];
  }

  override getOverviewData(): Observable<void> {
    return of(undefined);
  }

  override openEntityDialog(id?: string): void {
    const sampleMediaModel: IFormDialogData = {
      id: id
    }
    this.openDialog(SampleMediaDialogComponent, sampleMediaModel)
  }

  onSwitchChange(entity: SampleMedia) {
    entity.isActive = !entity.isActive;
    this.updateEntity(entity);
  }

  updateEntity(entity: SampleMedia) {
    this.sampleMediaService.update(entity).subscribe(
      result => {
        this.toastService.showSuccess('Media Status Updated', true);
      },
      error => {
        entity.isActive = !entity.isActive;
        this.toastService.showError(error);
      }
    );
  }

  override openEntityDeleteDialog(entity: SampleMedia): void {
    this.openDeleteDialog(DeleteDialog, entity, this.sampleMediaService, 'Media');
  }
}
